import React, { useEffect, useState } from 'react';
import { PunchOut } from '@hpi-sox/hpstellar-core';
import RadioButton from '../../../ui/components/configure-radio';
import RadioButtonCard from '../../../ui/components/radio-button-card';

import { Helpers } from '../../../core/src/helpers';
import { withError } from '../../../shared/components/error-boundary';
import usePage from '../../../hooks/usePage';
import useTemplateTheme from '../../../hooks/useTemplateTheme';
import { deselectRecommendConfig, verifyPicks, trackConfiguratorChange } from '../../util';

export const NONE_SELECTION = { name: 'None', sku: 'none', display: 'true', price: 0, promoPrice: 0 };
const VALUE_DELIMITER = '::';

/**
 * include path with value to help ensure radio values are unique
 * @param {*} path
 * @param {*} sku
 * @returns
 */
const getOptionValue = (path, sku) => {
    return `${path}${VALUE_DELIMITER}${sku}`;
};

/**
 *
 * @param {*} value
 * @returns
 */
const getOptionSku = value => {
    const [path, sku] = value.split(VALUE_DELIMITER);
    return sku;
};

const handleSelection =
    (updateComponent, setValue, options, specIndex, trackCustomMetric, ctoProductInitial) => value => {
        const sku = getOptionSku(value);
        setValue(value);
        updateComponent({ options }, state => {
            const { components } = state;
            const { baseConfig = {} } = components || {};
            const { specCategories, recommendedConfigs, catentryId } = baseConfig;
            const updatedSpec = specCategories[specIndex] || {};
            let selectedSpecName;
            //loops through and marked only one as selected
            updatedSpec.optionitems.forEach(s => {
                if (s.sku === sku) {
                    s.selected = 'true';
                    selectedSpecName = s.name;
                } else {
                    s.selected = 'false';
                }
            });
            //whenever a selection is all preconfigs should be deselected
            deselectRecommendConfig(recommendedConfigs);
            //verify selection against server
            const componentChanges = { ...components, verifyingPicks: true };
            verifyPicks(componentChanges, updateComponent);

            //add flag to for verifying to prevent extra price and shiptdate api calls
            return { components: componentChanges };
        });
    };

export default withError(
    ({
        options = [],
        path,
        specIndex,
        errorMessage,
        hasNoneSelection,
        textSize,
        trackCustomMetric,
        ctoProductInitial,
        baseConfig,
        GMPoints,
        direction,
        selectOptionElement = <span>Included In Price</span>,
        columnSize,
        variant,
        inputVariant,
        onModalClose = () => {},
        colorSwatchProps,
        v2 = false,
    }) => {
        const hasNone = hasNoneSelection === 'true';
        const radioOptions = hasNone ? [NONE_SELECTION, ...options] : options;
        const selectedOption = options.find(o => o.selected === 'true') || {};
        const productName = baseConfig && baseConfig.title;
        const { updateComponent } = usePage();
        const { css } = useTemplateTheme(['radio']);
        //buttons more responsive by keep it in state, verify picks will still be called and reconcile this
        useEffect(() => {
            const { sku } = selectedOption || {};
            const optionSku = getOptionSku(selectedValue);
            //if value changes from verify picks, update it
            if (optionSku !== sku) {
                setValue(hasNone && !sku ? getOptionValue(path, NONE_SELECTION.sku) : getOptionValue(path, sku));
            }
        }, [selectedOption]);
        const [selectedValue, setValue] = useState(
            hasNone && !selectedOption.sku
                ? getOptionValue(path, NONE_SELECTION.sku)
                : getOptionValue(path, selectedOption.sku),
        );
        return (
            <>
                <div
                    className={`radio-group col-${columnSize} ${errorMessage ? ' radio-error' : ''}${v2 ? ' radio-group-v2' : ''}`}
                >
                    {radioOptions.map((option, i) => {
                        const {
                            sku,
                            name,
                            promoPrice,
                            display,
                            errorText,
                            additionalText,
                            colorHex,
                            imageUrl,
                            gtmActions,
                            subFrequency,
                            pdpURL,
                        } = option;

                        const subFrequencyText = Helpers.getSubFrequencyText(subFrequency);
                        const gmPoints = GMPoints && (GMPoints[sku] || '0.00');
                        const optionSku = getOptionSku(selectedValue);
                        const checked = optionSku === sku;
                        const priceDifference =
                            Helpers.convertPriceToNumber(promoPrice) -
                            Helpers.convertPriceToNumber(selectedOption.promoPrice || 0);
                        const diffPrefix = priceDifference < 0 ? '-' : '+';
                        const info = checked
                            ? selectOptionElement
                            : Helpers.formatCurrency(
                                  Math.abs(priceDifference),
                                  `${diffPrefix} $`,
                                  true,
                                  false,
                                  subFrequencyText,
                              );
                        const CallCenterPunchOut = gmPoints ? (
                            <PunchOut className="cto-margin" grossMargin={gmPoints} />
                        ) : null;

                        return (
                            display === 'true' &&
                            (v2 ? (
                                <RadioButtonCard
                                    className={`radioButton${css ? ` ${css}-radio` : undefined}`}
                                    id={sku}
                                    key={`${sku}-${checked}-${i}`}
                                    label={name}
                                    info={
                                        <>
                                            {info}
                                            {CallCenterPunchOut}
                                        </>
                                    }
                                    checked={checked}
                                    to={pdpURL}
                                    value={getOptionValue(path, sku)}
                                    data-sku={sku}
                                    error={errorText}
                                    offerMessage={additionalText}
                                    colorHex={colorHex}
                                    infoClassName={!checked ? 'price' : undefined}
                                    textSize={textSize}
                                    productName={productName}
                                    gtmActions={gtmActions}
                                    direction={direction}
                                    variant={variant}
                                    inputVariant={inputVariant}
                                    onChange={val => {
                                        handleSelection(
                                            updateComponent,
                                            setValue,
                                            radioOptions,
                                            specIndex,
                                            trackCustomMetric,
                                            ctoProductInitial,
                                        )(val);
                                        onModalClose();
                                    }}
                                    isInpOptimized
                                    tabIndex="0"
                                    colorSwatchProps={{ ...colorSwatchProps, shape: 'square' }}
                                    groupName={path}
                                ></RadioButtonCard>
                            ) : (
                                <RadioButton
                                    className={`radioButton${css ? ` ${css}-radio` : undefined}`}
                                    id={sku}
                                    key={`${sku}-${checked}-${i}`}
                                    label={name}
                                    info={
                                        <>
                                            {info}
                                            {CallCenterPunchOut}
                                        </>
                                    }
                                    checked={checked}
                                    to={pdpURL}
                                    value={getOptionValue(path, sku)}
                                    variation="tile"
                                    data-sku={sku}
                                    error={errorText}
                                    offerMessage={additionalText}
                                    colorHex={colorHex}
                                    infoClassName={!checked ? 'price' : undefined}
                                    textSize={textSize}
                                    productName={productName}
                                    gtmActions={gtmActions}
                                    direction={direction}
                                    variant={variant}
                                    inputVariant={inputVariant}
                                    onChange={val => {
                                        handleSelection(
                                            updateComponent,
                                            setValue,
                                            radioOptions,
                                            specIndex,
                                            trackCustomMetric,
                                            ctoProductInitial,
                                        )(val);
                                        onModalClose();
                                    }}
                                    isInpOptimized
                                    tabIndex="0"
                                    colorSwatchProps={colorSwatchProps}
                                />
                            ))
                        );
                    })}
                </div>
            </>
        );
    },
);
